export type ScoreRingSize = 'sm' | 'md' | 'lg';

export interface ScoreRingProps {
  score: number;
  maxScore?: number;
  size?: ScoreRingSize;
  className?: string;
}

const sizeClasses: Record<ScoreRingSize, { outer: string; text: string; sub: string; stroke: number }> = {
  sm: { outer: 'w-9 h-9', text: 'text-sm',    sub: 'hidden', stroke: 2 },
  md: { outer: 'w-14 h-14', text: 'text-lg',  sub: 'text-[9px]',  stroke: 3 },
  lg: { outer: 'w-16 h-16', text: 'text-xl',  sub: 'text-[10px]', stroke: 3 },
};

function getRingColor(score: number, max: number): { border: string; text: string } {
  const pct = score / max;
  if (pct >= 0.7) return { border: 'border-emerald-400', text: 'text-emerald-700 dark:text-emerald-400' };
  if (pct >= 0.4) return { border: 'border-amber-400',   text: 'text-amber-800  dark:text-amber-400' };
  return              { border: 'border-rose-400',    text: 'text-rose-700   dark:text-rose-400' };
}

/**
 * Score ring (Direction B design language).
 * Displays score as a numeral inside a colored circular outline ring.
 */
export function ScoreRing({ score, maxScore = 50, size = 'md', className = '' }: ScoreRingProps) {
  const { outer, text, sub, stroke } = sizeClasses[size];
  const { border, text: textColor } = getRingColor(score, maxScore);

  return (
    <span
      className={`${outer} rounded-full border-${stroke} ${border} ${textColor} inline-flex flex-col items-center justify-center font-extrabold leading-none flex-shrink-0 ${className}`}
      style={{ borderWidth: `${stroke}px`, borderStyle: 'solid' }}
      title={`Score: ${score}/${maxScore}`}
    >
      <span className={text}>{score}</span>
      {sub !== 'hidden' && (
        <span className={`${sub} font-medium opacity-60 mt-0.5`}>/{maxScore}</span>
      )}
    </span>
  );
}
